/**
 * @module utils/date
 * @description Date and time utility functions using Luxon library.
 * Provides functions for date formatting, manipulation, comparison, and relative date calculations.
 */

import { DateTime, Duration } from 'luxon';
import { Sanity } from 'src/types/sanity';
import { Assets } from 'src/types/assets/index';
import { FlexDate } from 'src/types/utils';
import { pluralize } from './str';

/**
 * @function nowEpoch
 * @description Get current time as epoch milliseconds
 * @returns {number} Current time in milliseconds since Unix epoch
 */
export const nowEpoch = () => DateTime.now().toMillis();

/**
 * @function nowISO
 * @description Get current time as ISO 8601 string
 * @returns {string} Current time in ISO format
 */
export const nowISO = () => DateTime.now().toISO();

/**
 * @function nowYMD
 * @description Get current date in YYYY-MM-DD format
 * @returns {string} Current date as YYYY-MM-DD
 */
export const nowYMD = () => DateTime.now().toFormat('yyyy-MM-dd');

/**
 * @function daysISO
 * @description Add days to current time and return ISO string
 * @param {number} days - Number of days to add (negative for past dates)
 * @returns {string} ISO string of current time plus specified days
 */
export const daysISO = (days: number) => DateTime.now().plus({ days }).toISO();

/**
 * @function daysEpoch
 * @description Add days to current time and return epoch milliseconds
 * @param {number} days - Number of days to add (negative for past dates)
 * @returns {number} Epoch milliseconds of current time plus specified days
 */
export const daysEpoch = (days: number) => DateTime.now().plus({ days }).toMillis();

/**
 * @function hoursISO
 * @description Add hours to current time and return ISO string
 * @param {number} hours - Number of hours to add (negative for past times)
 * @returns {string} ISO string of current time plus specified hours
 */
export const hoursISO = (hours: number) => DateTime.now().plus({ hours }).toISO();

/**
 * @function hoursEpoch
 * @description Add hours to current time and return epoch milliseconds
 * @param {number} hours - Number of hours to add (negative for past times)
 * @returns {number} Epoch milliseconds of current time plus specified hours
 */
export const hoursEpoch = (hours: number) => DateTime.now().plus({ hours }).toMillis();

/**
 * @function normalizeDate
 * @description Convert flexible date input (string, number, Date) to Luxon DateTime object
 * @param {FlexDate} date - Date as string, number (epoch), or Date object
 * @returns {DateTime} Luxon DateTime object
 */
export const normalizeDate = (date: FlexDate): DateTime => {
  return (
    typeof date === 'string' || typeof date === 'number'
      ? DateTime.fromISO(date.toString())
      : DateTime.fromJSDate(date as Date)
  );
};

/**
 * @function getFirstDate
 * @description Get the earliest date from an array of dates
 * @param {FlexDate[]} dates - Array of dates to compare
 * @returns {FlexDate | undefined} Earliest date from the array, or undefined if array is empty
 */
export const getFirstDate = (dates: FlexDate[]): FlexDate | undefined => {
  if (!dates || dates.length === 0) return undefined;
  return dates.sort((a, b) => normalizeDate(a).diff(normalizeDate(b)).toMillis())[0];
};

/**
 * @function isDateBefore
 * @description Check if a date is before the specified reference time (defaults to now)
 * @param {FlexDate} date - Date to check
 * @param {DateTime} [now=DateTime.now()] - Reference time to compare against
 * @returns {boolean} True if date is before the reference time
 */
export const isDateBefore = (date: FlexDate, now: DateTime = DateTime.now()) => {
  if (!date) return false;

  try {
    const dateTime = normalizeDate(date);
    return dateTime.isValid && dateTime < now;
  } catch (e) {
    console.error('Error parsing date:', e);
    return false;
  }
};

/**
 * @function hasPackageExpired
 * @description Check if an asset package has expired based on its expiry date
 * @param {Sanity.Types.OrderAssetPackage} assetPackage - Asset package to check
 * @returns {boolean} True if package expiry date is in the past
 */
export const hasPackageExpired = (assetPackage: Sanity.Types.OrderAssetPackage): boolean => {
  return isDateBefore(assetPackage.expiry!);
};

/**
 * @function hasLinkExpired
 * @description Check if an asset package's download link has expired
 * @param {Sanity.Types.OrderAssetPackage} assetPackage - Asset package to check
 * @returns {boolean} True if link expiry date is in the past
 */
export const hasLinkExpired = (assetPackage: Sanity.Types.OrderAssetPackage): boolean => {
  return isDateBefore(assetPackage.linkExpiry!);
};

/**
 * @function hasExpired
 * @description Check if either the package or its link has expired
 * @param {Sanity.Types.OrderAssetPackage} assetPackage - Asset package to check
 * @returns {boolean} True if either package or link has expired
 */
export const hasExpired = (assetPackage: Sanity.Types.OrderAssetPackage): boolean => {
  return hasPackageExpired(assetPackage) || hasLinkExpired(assetPackage);
};

/**
 * @function getTimestamp
 * @description Format a date as a human-readable timestamp string (MMM dd yyyy @ hh:mm a)
 * @param {FlexDate} date - Date to format
 * @returns {string} Formatted timestamp string, or empty string if invalid
 */
export const getTimestamp = (date: FlexDate): string => {
  const dateTime = normalizeDate(date);

  if (!dateTime.isValid) return '';

  return dateTime.toFormat('MMM dd yyyy @ hh:mm a');
};

/**
 * @function getDateDiff
 * @description Calculate the duration difference between a date and reference time
 * @param {FlexDate} date - Date to compare
 * @param {DateTime} [now=DateTime.now()] - Reference time (defaults to current time)
 * @returns {Duration | null} Luxon Duration object with days/hours/minutes, or null if invalid
 */
export const getDateDiff = (date: FlexDate, now: DateTime = DateTime.now()): Duration | null => {
  const dateTime = (
    typeof date === 'string' || typeof date === 'number'
      ? DateTime.fromISO(date.toString())
      : DateTime.fromJSDate(date as Date)
  );

  if (!dateTime.isValid) return null;

  return dateTime.diff(now, ['days', 'hours', 'minutes']);
};

/**
 * @function getRelativeDate
 * @description Format a date as a human-readable relative string (e.g., "In 3 days", "In 2 hours")
 * Uses asset expiry thresholds to determine appropriate display format.
 * @param {Object} params - Parameters object
 * @param {FlexDate} params.date - Date to format
 * @param {DateTime} [params.now=DateTime.now()] - Reference time for comparison
 * @param {boolean} [params.showTimestamp=true] - Whether to include full timestamp prefix
 * @returns {string} Formatted relative date string
 */
export const getRelativeDate = ({
  date,
  now = DateTime.now(),
  showTimestamp = true,
}: {
  date: FlexDate,
  now?: DateTime,
  showTimestamp?: boolean
}): string => {
  if (!date) return '(never)';

  try {
    const diff = getDateDiff(date, now);
    if (!diff) return '(never)';

    const days = Math.floor(diff.days);
    const hours = Math.floor(diff.hours);
    const minutes = Math.floor(diff.minutes);

    const pretty = showTimestamp ? getTimestamp(date) : '';

    // If more than threshold days, show full date
    // if (days > threshold) {
    //   return pretty;
    // }

    const prefix = pretty ? `${pretty} - In ` : '';

    // If 2-7 days, show "In X days"
    if (days >= Assets.Data.ExpiryThreshold[Assets.Types.ExpiryStatus.WARNING]) {
      return `${prefix}${days} ${pluralize(days, 'day')}`;
    }

    // If less than 1 day, show hours and minutes
    if (days === Assets.Data.ExpiryThreshold[Assets.Types.ExpiryStatus.CRITICAL]) {
      return `${prefix}${hours + 24} hours`;
    }

    if (hours > 0) {
      return `${prefix}${hours} ${pluralize(hours, 'hour')}`;
    }

    if (minutes > 0) {
      return `${prefix}${minutes} ${pluralize(minutes, 'minute')}`;
    }

    return 'Expired';
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'Invalid date';
  }
};

/**
 * @function formatRelativeDate
 * @description Format a date as relative string (wrapper around getRelativeDate with current time)
 * @param {Object} params - Parameters object
 * @param {FlexDate} params.date - Date to format
 * @param {boolean} [params.showTimestamp=true] - Whether to include full timestamp prefix
 * @returns {string} Formatted relative date string
 */
export const formatRelativeDate = ({
  date,
  showTimestamp = true,
}: {
  date: FlexDate,
  showTimestamp?: boolean,
}): string => {
  return getRelativeDate({ date, showTimestamp });
};
